/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* eslint-disable security/detect-unsafe-regex */
import * as React from 'react';

import { IDobbiesFooterNewsletterProps, INewsletterheadingData } from './dobbies-footer-newsletter.props.autogenerated';

import { Button, IModuleProps, INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { vSIInitiateRestCallAsync } from '../../dotdigital-extension/DataActionExtension.g';
import DobbiesModal from './components/dobbies-modal';
import axios, { AxiosRequestConfig } from 'axios';
import { DOT_DIGITAL_API_URL } from '../../themes/dobbies/utilities/constants';
import { IDataLayerEvent, pushToDataLayer } from '../../themes/dobbies/utilities/analytics';

export interface IDobbiesFooterNewsletterViewProps extends IDobbiesFooterNewsletterProps<{}> {
    moduleProps: IModuleProps;
    contentProps: INodeProps;
    newsletterheading: React.ReactNode;
    newsletterDescription: React.ReactNode;
    joinNow: React.ReactNode;
    subscribeForm: React.ReactNode | undefined;
}
export interface IDobbiesFooterNewsletterState {
    isPopupOpen: boolean;
    firstName: string;
    lastName: string;
    emailAddress: string;
    privacyPolicyChecked: boolean;
    firstNameError: boolean;
    lastNameError: boolean;
    emailError: boolean;
    requiredError: boolean;
    emailValidationError: boolean;
    privacyPolicyError: boolean;
    recaptchaError: boolean;
    responseReceived: boolean;
    responseError: boolean;
    reCaptchaChecked: boolean;
}
export interface ISubscribeNewsletterForm {
    FormWrapper: INodeProps;
    FormContainer: INodeProps;
    nameInput: React.ReactNode;
    emailInput: React.ReactNode;
    checkboxPrivacyPolicy: React.ReactNode;
    subscribeButtton: React.ReactNode;
}
export interface IErrorMessage {
    message: string;
}
interface IDataField {
    key: string;
    value: string;
}
// Contact created Result
export interface IContactCreatedResponse {
    id: number;
    email: string;
    optInType: string;
    emailType: string;
    dataFields: IDataField[];
    status: string;
}
// To add contact in program
interface IPreferenceProgram {
    programId: number;
    contacts: number[];
}
/**
 *
 * DobbiesFooterNewsletter component
 * @extends {React.PureComponent<IDobbiesFooterNewsletterProps<{}>, IDobbiesFooterNewsletterState>}
 */
class DobbiesFooterNewsletter extends React.PureComponent<IDobbiesFooterNewsletterProps<{}>, IDobbiesFooterNewsletterState> {
    private _FirstNameInputRef: React.RefObject<HTMLInputElement>;
    private _LastNameInputRef: React.RefObject<HTMLInputElement>;
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _PrivacyPolicyRef: React.RefObject<HTMLInputElement>;
    private hasFirstNameValue: boolean = false;
    private hasLastNameValue: boolean = false;
    private hasEmailAddressValue: boolean = false;
    private hasFormContainValues: boolean = false;
    private requiredErrorMessage: string | undefined;
    private emailValidationErrorMessage: string | undefined;
    private privacyPolicyErrorMessage: string | undefined;
    private responseMessage: string | undefined;

    constructor(props: IDobbiesFooterNewsletterProps<{}>, state: IDobbiesFooterNewsletterState) {
        super(props);
        this._FirstNameInputRef = React.createRef();
        this._LastNameInputRef = React.createRef();
        this._EmailAddressInputRef = React.createRef();
        this._PrivacyPolicyRef = React.createRef();
        this._handleSubscribe = this._handleSubscribe.bind(this);

        this.state = {
            isPopupOpen: false,
            firstName: '',
            lastName: '',
            emailAddress: '',
            privacyPolicyChecked: false,
            firstNameError: false,
            lastNameError: false,
            emailError: false,
            requiredError: false,
            emailValidationError: false,
            privacyPolicyError: false,
            recaptchaError: false,
            responseReceived: false,
            responseError: false,
            reCaptchaChecked: false
        };
    }

    public render(): JSX.Element | null {
        const {
            config: { newsletterheading, newsletterDescription, className },
            resources: { joinNowLabel, joinNowButtonAriaLabel }
        } = this.props;
        const viewProps = {
            ...this.props,
            moduleProps: { className: classnames('ms-dobbies-footer-newsletter', className), moduleProps: this.props, tag: 'div' },
            contentProps: { className: 'ms-dobbies-footer-newsletter__content', tag: 'div' },
            newsletterheading: this._getHeading(newsletterheading, className),
            newsletterDescription: this._getNewsLetterText(newsletterDescription, className),
            joinNow: this._getJoinNowButton(joinNowLabel, joinNowButtonAriaLabel, className),
            subscribeForm: this.state.isPopupOpen && this._renderPopUp()
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /* Following function renders heading of newsletter */
    private _getHeading = (newsletterheading: INewsletterheadingData | undefined, className: string | undefined): React.ReactNode => {
        return (
            <strong>
                {newsletterheading && (
                    <Msdyn365.Text
                        {...newsletterheading}
                        tag={newsletterheading.tag || 'h4'}
                        className={classnames(className, 'ms-dobbies-footer-newsletter__heading')}
                        text={newsletterheading.text}
                    />
                )}
            </strong>
        );
    };

    /* Following function renders description of newsletter */
    private _getNewsLetterText = (newsletterDescription: Msdyn365.RichText | undefined, className: string | undefined): React.ReactNode => {
        return (
            <div className={classnames(className, 'ms-dobbies-footer-newsletter__description')}>
                {newsletterDescription && <Msdyn365.RichTextComponent text={newsletterDescription} />}
            </div>
        );
    };

    /* Following function renders Join Now Button */
    private _getJoinNowButton = (
        joinNowLabel: string | undefined,
        joinNowAriaLabel: string | undefined,
        className: string | undefined
    ): React.ReactNode => {
        return (
            <div className='newslatter-signup'>
                <input
                    id='newslatter-email'
                    type='email'
                    onChange={e => this.setState({ emailAddress: e.target.value })}
                    className='newslatter-signup-email'
                    placeholder='Enter your email address…'
                />
                <Button
                    className={classnames(className, 'ms-dobbies-footer-newsletter__btn-join-now msc-btn')}
                    title={joinNowAriaLabel}
                    color='secondary'
                    onClick={this._togglePopup}
                >
                    {joinNowLabel}
                </Button>
            </div>
        );
    };
    // Open popup
    private _togglePopup = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        this.setState({ isPopupOpen: true });
    };
    // Open popup by clicking on join now button
    private _renderPopUp = () => {
        return (
            <DobbiesModal
                isOpen={this.state.isPopupOpen}
                modalTitle={this._renderPopupTitle()}
                modalBodyElement={this._renderPopupBody()}
                modalFooter={this._renderPopupFooter()}
                className='ms-dobbies-newsletter-popup signup-redesign-newsletter-popup'
            />
        );
    };

    // Render Popup title
    private _renderPopupTitle = () => {
        const { popUpHeading } = this.props.config;
        return (
            <Node className='popup-header' tag='div'>
                <Node role='button' onClick={this._closePopup} className='newsletter-popup-cross' />
                {popUpHeading && (
                    <Msdyn365.Text {...popUpHeading} tag={popUpHeading.tag} className={'popup__heading'} text={popUpHeading.text} />
                )}
            </Node>
        );
    };
    // Render popup footer
    private _renderPopupFooter = () => {
        return <></>;
    };
    // Render popup body to show input fields
    private _renderPopupBody = () => {
        const subscribeResponse = this._getSubscribeResponse();
        const subscribeForm = this._renderForm();
        const { FormWrapper, FormContainer, nameInput, emailInput, checkboxPrivacyPolicy, subscribeButtton } = subscribeForm;
        return (
            <Node {...FormWrapper}>
                {subscribeResponse}
                <Node {...FormContainer}>
                    <p>Please enter your details</p>
                    {nameInput}
                    {emailInput}
                    {checkboxPrivacyPolicy}
                    {subscribeButtton}
                    <Button onClick={this._closePopup} className='msc-btn close-popup'>
                        {this.props.resources.cancelButtonTitle}
                    </Button>
                </Node>
            </Node>
        );
    };
    // Close popup and reset state
    private _closePopup = () => {
        this.setState({
            isPopupOpen: false,
            firstName: '',
            lastName: '',
            emailAddress: '',
            privacyPolicyChecked: false,
            firstNameError: false,
            lastNameError: false,
            emailError: false,
            requiredError: false,
            emailValidationError: false,
            privacyPolicyError: false,
            recaptchaError: false,
            responseReceived: false,
            responseError: false,
            reCaptchaChecked: false
        });
    };

    // Get all input fields and return as form fields
    private _renderForm(): ISubscribeNewsletterForm {
        return {
            FormWrapper: {
                className: 'newsletter-subscription',
                tag: 'div'
            },
            FormContainer: {
                className: 'newsletter-subscription-div',
                tag: 'div'
            },
            nameInput: this._renderFirstAndLastNameInput(),
            emailInput: this._renderEmailInput(),
            checkboxPrivacyPolicy: this._renderCheckboxPrivacyPolicy(),
            subscribeButtton: this._renderSubscribeButton()
        };
    }

    private _renderFirstAndLastNameInput(): React.ReactNode {
        return (
            <div className='first-and-last-name-input'>
                <div className='name-input-error'>
                    <span className='first-name-input-error'>{this.state.firstNameError ? this._renderAlert('first-name-input') : ''}</span>
                    <span className='last-name-input-error'>{this.state.lastNameError ? this._renderAlert('last-name-input') : ''}</span>
                </div>
                <div className='first-name-input'>
                    <input
                        ref={this._FirstNameInputRef}
                        type='text'
                        onChange={this._handleChange('firstName')}
                        aria-label='First Name'
                        className='first-name-input-text'
                        placeholder={this.props.resources.firstNamePlaceholderText}
                        value={this.state.firstName}
                        required
                    />
                </div>
                <div className='last-name-input'>
                    <input
                        ref={this._LastNameInputRef}
                        type='text'
                        onChange={this._handleChange('lastName')}
                        aria-label='Last Name'
                        className='last-name-input-text'
                        placeholder={this.props.resources.lastNamePlaceholderText}
                        value={this.state.lastName}
                        required
                    />
                </div>
            </div>
        );
    }

    private _renderEmailInput(): React.ReactNode {
        return (
            <div className='email-address-input'>
                {this.state.emailError || this.state.emailValidationError ? this._renderAlert('email-address-input') : ''}
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this._handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    placeholder={this.props.resources.emailAddressPlaceholderText}
                    value={this.state.emailAddress}
                    required
                />
            </div>
        );
    }

    private _isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private _renderCheckboxPrivacyPolicy(): React.ReactNode {
        return (
            <div className='checkbox-privacy-policy'>
                {this.state.privacyPolicyError ? this._renderAlert('checkbox-privacy-policy') : ''}
                <div className='checkbox-privacy-policy-content'>
                    <input
                        ref={this._PrivacyPolicyRef}
                        type='checkbox'
                        className='privacy-policy-text'
                        aria-label='Accept Privacy Policy'
                        aria-checked='false'
                        onChange={this._handleChange('privacyPolicy')}
                        id='privacyPolicyCheckbox'
                    />
                    <span className='checkbox-privacy-policy-text'>
                        <p>
                            I accept the <a href={this.props.config.privacyPolicyLink}>Privacy Policy</a>
                        </p>
                    </span>
                </div>
            </div>
        );
    }

    private _isDisabled(): boolean {
        return this.state.firstName && this.state.lastName && this.state.emailAddress && this.state.privacyPolicyChecked ? false : true;
    }
    private _renderSubscribeButton(): React.ReactNode {
        return (
            <div>
                <Button
                    className='subscribe-button'
                    title={this.props.resources.subscribeButtonLabel}
                    color='primary'
                    disabled={this._isDisabled()}
                    onClick={this._handleSubscribe}
                >
                    {this.props.resources.subscribeButtonLabel}
                </Button>
            </div>
        );
    }
    // Store input fields value to state
    private _handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        switch (name) {
            case 'firstName': {
                this.setState({
                    firstName: value,
                    firstNameError: false,
                    requiredError: false,
                    emailValidationError: false,
                    privacyPolicyError: false
                });
                break;
            }
            case 'lastName': {
                this.setState({
                    lastName: value,
                    lastNameError: false,
                    requiredError: false,
                    emailValidationError: false,
                    privacyPolicyError: false
                });
                break;
            }
            case 'emailAddress': {
                this.setState({
                    emailAddress: value,
                    emailError: false,
                    requiredError: false,
                    emailValidationError: false,
                    privacyPolicyError: false
                });
                break;
            }
            case 'privacyPolicy': {
                const isChecked =
                    this._PrivacyPolicyRef.current && this._PrivacyPolicyRef.current.checked
                        ? this._PrivacyPolicyRef.current.checked
                        : false;
                this.setState({
                    privacyPolicyChecked: isChecked,
                    requiredError: false,
                    emailValidationError: false,
                    privacyPolicyError: false
                });
                break;
            }
            default: {
                this.setState({ requiredError: false, emailValidationError: false, privacyPolicyError: false });
            }
        }
    };
    // Show error message on invalid email or if checkbox is not checked
    private _renderAlert(className: string): JSX.Element {
        const inputBoxErrors = this.state.firstNameError || this.state.lastNameError || this.state.emailError;
        if (inputBoxErrors) {
            this.requiredErrorMessage = this.props.resources.requiredTextErrorMessage;
        }
        if (this.state.emailAddress !== '' && this.state.emailValidationError) {
            this.emailValidationErrorMessage = this.props.resources.emailValidationErrorMessage;
        }
        if (!this.state.privacyPolicyChecked) {
            this.privacyPolicyErrorMessage = this.props.resources.privacyPolicyErrorMessage;
        }
        const requiredError = this.state.requiredError;
        const emailValidationError = this.state.emailValidationError;
        const privacyPolicyError = this.state.privacyPolicyError;
        return (
            <>
                {requiredError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {this.requiredErrorMessage && (
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.requiredErrorMessage}
                            </>
                        )}
                    </span>
                )}
                {emailValidationError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {this.emailValidationErrorMessage && (
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.emailValidationErrorMessage}
                            </>
                        )}
                    </span>
                )}
                {privacyPolicyError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {this.privacyPolicyErrorMessage && (
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.privacyPolicyErrorMessage}
                            </>
                        )}
                    </span>
                )}
            </>
        );
    }

    // Get API response
    private _getSubscribeResponse(): React.ReactNode {
        return this.state.responseReceived && this.responseMessage ? (
            <div className={`response-message ${this.state.responseError ? 'response-error' : ''}`}>
                <p className='response-message-content' id='response-message'>
                    {this.responseMessage}
                </p>
            </div>
        ) : null;
    }

    private _hasFirstNameValue = () => {
        return this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value ? true : false;
    };

    private _hasLastNameValue = () => {
        return this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value ? true : false;
    };

    private _hasEmailAddressValue = () => {
        return this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value ? true : false;
    };

    private _hasFormContainValues = () => {
        return this.hasFirstNameValue && this.hasLastNameValue && this.hasEmailAddressValue ? true : false;
    };

    private subscribeToDotDigital = (email: string) => {
        var data = JSON.stringify({
            emailDataRequest: {
                Identifiers: {
                    Email: email
                },
                ChannelProperties: {
                    Email: {
                        ResubscribeOptions: {
                            ResubscribeWithNoChallenge: true
                        },
                        EmailType: 'html',
                        OptInType: 'single',
                        Status: 'subscribed'
                    }
                }
            }
        });

        const headers = {
            accept: 'application/json',
            'accept-language': 'en-GB',
            'content-type': 'application/json',
            contenttype: 'application/json',
            oun: this.props.context.request.apiSettings.oun
        };
        if (this.props.context.request.user.isAuthenticated) {
            headers['authorization'] = `id_token ${this.props.context.actionContext.requestContext.user.token}`;
        }
        var config: AxiosRequestConfig = {
            method: 'post',
            url: DOT_DIGITAL_API_URL,
            headers: headers,
            data: data
        };

        axios(config)
            .then(async response => {
                this.responseMessage = this.props.resources.programSuccessMessage;
                this.setState({
                    responseReceived: true,
                    responseError: false,
                    firstName: '',
                    lastName: '',
                    emailAddress: '',
                    privacyPolicyChecked: false,
                    requiredError: false,
                    emailValidationError: false,
                    recaptchaError: false
                });
                this._trackNewsletterSubscription();
                this._clearValues();
            })
            .catch(error => {
                console.log(error);
                if (error) {
                    // Address book isn't found or any other issue So set error message
                    this.responseMessage = this.props.resources.programErrorMessage;
                    this.setState({ responseReceived: true, responseError: true });
                }
            });
    };

    private async _handleSubscribe(
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>
    ): Promise<void> {
        this.hasFirstNameValue = this._hasFirstNameValue();
        this.hasLastNameValue = this._hasLastNameValue();
        this.hasEmailAddressValue = this._hasEmailAddressValue();
        const email = this.hasEmailAddressValue ? this._EmailAddressInputRef.current!.value : '';
        if (!this.hasFirstNameValue) {
            this.setState({
                firstNameError: true,
                requiredError: true
            });
        } else if (!this.hasLastNameValue) {
            this.setState({ lastNameError: true, requiredError: true });
        } else if (!this.hasEmailAddressValue) {
            this.setState({ emailError: true, requiredError: true });
        } else if (!this._isEmailValid(email)) {
            this.setState({ emailValidationError: true });
        } else if (!this._PrivacyPolicyRef.current!.checked) {
            this.setState({ privacyPolicyChecked: false, privacyPolicyError: true });
        } else {
            this.hasFormContainValues = this._hasFormContainValues();
            if (this.hasFormContainValues && this.state.privacyPolicyChecked && !this.state.emailValidationError) {
                try {
                    // Make subscribe newsletter data-action call here with user data
                    const contact = {
                        email: this.state.emailAddress,
                        emailType: 'Html',
                        dataFields: [
                            { key: 'FIRSTNAME', value: this.state.firstName },
                            { key: 'LASTNAME', value: this.state.lastName }
                        ],
                        status: 'Subscribed'
                    };
                    const {
                        context: { actionContext },
                        config: { programID },
                        resources: { programSuccessMessage }
                    } = this.props;
                    let isError = false;
                    if (programID) {
                        // call api here to create contact
                        const response = await vSIInitiateRestCallAsync(
                            { callerContext: actionContext },
                            'DotDigital',
                            'post',
                            "{'cache-control':'no-cache' , 'Accept':'application/json'}",
                            JSON.stringify(contact),
                            '',
                            `v2/contacts`
                        );
                        if (response.result) {
                            const responseResult = JSON.parse(response.result);
                            const errorMessage: IErrorMessage = responseResult;
                            if (errorMessage.message && errorMessage.message.length > 0) {
                                // Address book isn't found or any other issue So set error message
                                isError = true;
                            } else {
                                // Contact created so now add contact in program
                                // Get contact id
                                const contactCreated: IContactCreatedResponse = responseResult;
                                if (contactCreated.id) {
                                    // add this id in given programId
                                    const program: IPreferenceProgram = {
                                        contacts: [contactCreated.id],
                                        programId: programID
                                    };
                                    const programResponse = await vSIInitiateRestCallAsync(
                                        { callerContext: actionContext },
                                        'DotDigital',
                                        'post',
                                        "{'cache-control':'no-cache' , 'Accept':'application/json'}",
                                        JSON.stringify(program),
                                        '',
                                        `v2/programs/enrolments`
                                    );
                                    if (programResponse.result) {
                                        const programResponseResult = JSON.parse(programResponse.result);
                                        const error: IErrorMessage = programResponseResult;
                                        if (error.message && error.message.length > 0) {
                                            // Error in adding contact in Program
                                            // display error alert
                                            isError = true;
                                        } else {
                                            this.responseMessage = programSuccessMessage;
                                            this._trackNewsletterSubscription();
                                            // Contact has been added to address book
                                            this.setState({
                                                responseReceived: true,
                                                responseError: false,
                                                firstName: '',
                                                lastName: '',
                                                emailAddress: '',
                                                privacyPolicyChecked: false,
                                                requiredError: false,
                                                emailValidationError: false,
                                                recaptchaError: false
                                            });
                                            this._clearValues();
                                        }
                                    } else {
                                        isError = true;
                                    }
                                } else {
                                    isError = true;
                                }
                            }
                        }
                    } else {
                        // display error alert
                        isError = true;
                    }
                    if (isError) {
                        try {
                            this.subscribeToDotDigital(this.state.emailAddress);
                        } catch (e) {
                            console.log('Error subscribing to DotDigital', e);
                        }
                    }
                } catch (e) {
                    // @ts-ignore
                    this.props.telemetry.error(e);
                    try {
                        this.subscribeToDotDigital(this.state.emailAddress);
                    } catch (e) {
                        console.log('Error subscribing to DotDigital', e);
                    }
                }
            }
        }
    }

    private _clearValues(): void {
        if (this._PrivacyPolicyRef.current) {
            this._PrivacyPolicyRef.current.checked = false;
        }
    }

    private _trackNewsletterSubscription(): void {
        const dataEvent: IDataLayerEvent = {
            event: 'newsletter_subscription',
            method: 'Footer'
        };
        pushToDataLayer(dataEvent);
    }
}

export default DobbiesFooterNewsletter;
